// The composite the owner asked for: one pass that knows DEPTH, so the
// edges can be any thickness and the shading can be real occlusion.
//
// Tonight's shipped effects (loader/streaming/surfaceShading.js) read the
// derivatives of the surface being shaded. That is free - no targets, no
// extra pass - but it can only see what one surface does, so a silhouette
// against a DIFFERENT object is invisible to it and an edge is always
// exactly one pixel.
//
// This does it properly:
//   1. a depth + view-normal pass over the same scene (the only extra
//      geometry work), into a target with a real depth texture;
//   2. the beauty pass as usual, into a colour target;
//   3. one screen pass: occlusion from the depth buffer, edges from depth
//      AND normal discontinuities at a chosen PIXEL RADIUS, multiplied
//      into the colour.
//
// Memory at 1440x900 is ~17 MB (colour + normal + depth + AO), which is
// 0.4% of the SpaceX load ceiling - the cost here is the extra geometry
// pass, not the buffers, which is why `freezeWhileMoving` exists: the
// prepass and the AO are recomputed when the camera SETTLES and held
// while it moves, and the cheap shipped effect covers the moving frames.
//
// This is behind a flag and off by default.
import {
  WebGLRenderTarget, DepthTexture, UnsignedIntType, DepthFormat,
  MeshNormalMaterial, ShaderMaterial, OrthographicCamera, PlaneGeometry, Mesh,
  Scene, NearestFilter, LinearFilter, RGBAFormat, DoubleSide, Vector2,
} from 'three';

const VERT = `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

// One screen pass. Occlusion and edges both come out of the same depth
// and normal reads, so the taps are shared.
const FRAG = `
precision highp float;
uniform sampler2D tColor;
uniform sampler2D tNormal;
uniform sampler2D tDepth;
uniform vec2  uTexel;        // 1 / size
uniform float uNear;
uniform float uFar;
uniform float uEdge;         // 0..1 strength
uniform float uEdgePx;       // thickness, in pixels
uniform float uAo;           // 0..1 strength
uniform float uAoRadiusWorld;   // feet, not pixels
uniform float uProjScale;       // world unit -> pixels at one unit of depth
uniform float uDepthEdge;    // how big a depth break counts, relative to distance
varying vec2 vUv;

float viewZ( vec2 uv ) {
  float d = texture2D( tDepth, uv ).x;
  // perspective depth -> view-space distance
  return ( 2.0 * uNear * uFar ) / ( uFar + uNear - ( d * 2.0 - 1.0 ) * ( uFar - uNear ) );
}
vec3 viewNormal( vec2 uv ) { return texture2D( tNormal, uv ).xyz * 2.0 - 1.0; }

void main() {
  vec4 base = texture2D( tColor, vUv );
  // Nothing was drawn here - sky, or past the far plane. Neither an edge
  // nor occlusion means anything without geometry, and reading them
  // anyway covered the sky in a grid of marks the first time round.
  float raw = texture2D( tDepth, vUv ).x;
  if ( raw >= 0.999999 ) { gl_FragColor = base; return; }

  float z = viewZ( vUv );
  vec3  n = viewNormal( vUv );

  float shade = 1.0;

  if ( uEdge > 0.0 ) {
    // A ring at the chosen pixel radius: thickness is the radius, which
    // is why this route can give the owner 1px, 2px or 3px while line
    // geometry is stuck at 1 (gl_lineWidth is clamped to 1 everywhere).
    vec2 r = uTexel * uEdgePx;
    // Opposite pairs: a surface running away from the camera steps in
    // depth just as hard as a silhouette does, but it steps EVENLY -
    // the two sides of a pair cancel. A real break does not.
    float zL = viewZ( vUv - vec2( r.x, 0.0 ) ); float zR = viewZ( vUv + vec2( r.x, 0.0 ) );
    float zD = viewZ( vUv - vec2( 0.0, r.y ) ); float zU = viewZ( vUv + vec2( 0.0, r.y ) );
    float breakX = abs( zL + zR - 2.0 * z );
    float breakY = abs( zD + zU - 2.0 * z );
    float dSum = ( breakX + breakY ) / max( z, 1e-3 );
    float nSum = ( 1.0 - max( dot( n, viewNormal( vUv - vec2( r.x, 0.0 ) ) ), 0.0 ) )
               + ( 1.0 - max( dot( n, viewNormal( vUv + vec2( r.x, 0.0 ) ) ), 0.0 ) )
               + ( 1.0 - max( dot( n, viewNormal( vUv - vec2( 0.0, r.y ) ) ), 0.0 ) )
               + ( 1.0 - max( dot( n, viewNormal( vUv + vec2( 0.0, r.y ) ) ), 0.0 ) );
    float depthEdge = smoothstep( uDepthEdge, uDepthEdge * 4.0, dSum );
    float normalEdge = smoothstep( 0.5, 1.6, nSum );
    shade *= 1.0 - uEdge * max( depthEdge, normalEdge );
  }

  // viewZ() already returns a POSITIVE view-space distance, so this is
  // the depth to project the world radius at. (blP belongs to the other
  // shader - surfaceShading.js - and does not exist here.)
  float blDist = max( z, 1e-3 );
  if ( uAo > 0.0 ) {
    // Occlusion from the depth buffer: how much of a small disc around
    // this pixel sits IN FRONT of it. Cheap, stable, and unlike the
    // derivative version it can see a neighbouring object.
    // The sampling radius is a WORLD distance projected to pixels at
    // this fragment's depth, not a fixed pixel count. With a pixel
    // radius, a 200 ft roof seen from far away sampled geometry inches
    // apart in world terms and showed nothing at all, while the same
    // roof up close was sampled feet apart - the effect changed with
    // zoom, which is exactly what occlusion must not do. Clamped so a
    // fragment close to the camera cannot ask for a huge screen radius.
    float blRadPx = clamp( uAoRadiusWorld * uProjScale / blDist, 2.0, 64.0 );
    float occ = 0.0;
    const int TAPS = 12;
    for ( int i = 0; i < TAPS; i ++ ) {
      float a = float( i ) * 2.39996; // golden angle
      float rad = sqrt( ( float( i ) + 0.5 ) / float( TAPS ) ) * blRadPx;
      vec2 o = vec2( cos( a ), sin( a ) ) * uTexel * rad;
      vec2 uv2 = vUv + o;
      if ( texture2D( tDepth, uv2 ).x >= 0.999999 ) continue;   // sky occludes nothing
      float zs = viewZ( uv2 );
      float diff = z - zs;                        // positive: the sample is nearer
      float range = clamp( 1.0 - abs( diff ) / ( z * 0.04 ), 0.0, 1.0 );
      occ += step( z * 0.002, diff ) * range;
    }
    occ /= float( TAPS );
    shade *= 1.0 - uAo * occ;
  }

  gl_FragColor = vec4( base.rgb * shade, base.a );
}
`;

export const compositeState = {
  enabled: false,
  edge: 0.85,
  edgePx: 1,
  ao: 0.6,
  aoRadiusWorld: 3,          // feet: a parapet, an upstand, a duct
  depthEdge: 0.0025,
  freezeWhileMoving: true,
};

export default class SurfaceComposite {
  constructor(renderer) {
    this.renderer = renderer;
    this.size = { w: 0, h: 0 };
    this.color = null;
    this.normal = null;
    this.normalMaterial = new MeshNormalMaterial();
    // The scene is drawn with the app's clipping planes, so the prepass
    // has to obey them or the edges would trace geometry the beauty pass
    // has cut away.
    this.normalMaterial.clipping = true;
    this.normalMaterial.side = DoubleSide;
    this.quadScene = new Scene();
    this.quadCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.material = new ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        tColor: { value: null }, tNormal: { value: null }, tDepth: { value: null },
        uTexel: { value: new Vector2(1 / 1024, 1 / 1024) },
        uNear: { value: 0.1 }, uFar: { value: 1000 },
        uEdge: { value: 0 }, uEdgePx: { value: 1 },
        uAo: { value: 0 }, uAoRadiusWorld: { value: 3 }, uProjScale: { value: 500 },
        uDepthEdge: { value: 0.0025 },
      },
    });
    this.quad = new Mesh(new PlaneGeometry(2, 2), this.material);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);
    this._normalValidAt = null;    // camera signature the normal pass was taken at
  }

  _ensure(w, h) {
    if (this.color && this.size.w === w && this.size.h === h) return;
    this.dispose(true);
    this.size = { w, h };
    this.color = new WebGLRenderTarget(w, h, { format: RGBAFormat, minFilter: LinearFilter, magFilter: LinearFilter, depthBuffer: true });
    // Plain 24-bit depth, not depth+stencil: a depth-stencil texture
    // read through an ordinary sampler2D gave garbage under ANGLE and
    // SwiftShader, which is what put a grid of marks over the sky.
    const depth = new DepthTexture(w, h);
    depth.format = DepthFormat;
    depth.type = UnsignedIntType;
    this.normal = new WebGLRenderTarget(w, h, { format: RGBAFormat, minFilter: NearestFilter, magFilter: NearestFilter, depthBuffer: true });
    this.normal.depthTexture = depth;
    this.material.uniforms.tColor.value = this.color.texture;
    this.material.uniforms.tNormal.value = this.normal.texture;
    this.material.uniforms.tDepth.value = depth;
    this.material.uniforms.uTexel.value.set(1 / w, 1 / h);
  }

  // A cheap signature of where the camera is, so the prepass can be held
  // while nothing has moved.
  static cameraKey(camera) {
    const p = camera.position; const q = camera.quaternion;
    return `${p.x.toFixed(3)},${p.y.toFixed(3)},${p.z.toFixed(3)},${q.x.toFixed(4)},${q.y.toFixed(4)},${q.z.toFixed(4)},${q.w.toFixed(4)},${camera.zoom}`;
  }

  // -> true when it drew the frame, false when the caller should draw
  // normally (not enabled, or nothing to do).
  // `target` is where the composited picture lands: null for the canvas,
  // or a render target when the composite is running as a PASS inside the
  // composer chain so that outlines can be drawn over it (compositePass.js).
  render(scene, camera, { moving = false, target = null } = {}) {
    if (!compositeState.enabled) return false;
    // getSize calls target.set(), so it has to be a real Vector2 - a
    // plain object throws inside the render loop and the frame is lost.
    const size = this.renderer.getDrawingBufferSize(this._size || (this._size = new Vector2()));
    const w = Math.max(1, Math.floor(size.x));
    const h = Math.max(1, Math.floor(size.y));
    if (!w || !h) return false;
    this._ensure(w, h);

    const u = this.material.uniforms;
    u.uEdge.value = compositeState.edge;
    u.uEdgePx.value = Math.max(1, compositeState.edgePx);
    u.uAo.value = compositeState.ao;
    u.uAoRadiusWorld.value = compositeState.aoRadiusWorld;
    // How many pixels a world unit covers at one unit of depth:
    // 0.5 * viewportHeight * projection[1][1].
    u.uProjScale.value = 0.5 * h * camera.projectionMatrix.elements[5];
    u.uDepthEdge.value = compositeState.depthEdge;
    u.uNear.value = camera.near;
    u.uFar.value = camera.far;

    const key = SurfaceComposite.cameraKey(camera);
    const stale = this._normalValidAt !== key;
    const skipPrepass = compositeState.freezeWhileMoving && moving && this._normalValidAt !== null;
    // A frozen prepass belongs to the camera it was taken at. Occlusion
    // survives that - it is a soft, low-frequency thing and a frame or
    // two of lag does not read - but EDGES do not: drawn from a stale
    // depth buffer they sit beside the shapes they belong to and smear.
    // So while the prepass is being held, the edges are simply not
    // drawn, and the shipped derivative edges (which cost nothing and
    // need no buffer) are what the moving frames show.
    if (skipPrepass && stale) u.uEdge.value = 0;

    const prevTarget = this.renderer.getRenderTarget();
    // 1. depth + normal, only when it is worth taking
    if (stale && !skipPrepass) {
      const prevOverride = scene.overrideMaterial;
      // The sky dome is a real mesh, so the prepass would happily give it
      // normals and depth - and then every seam of its tessellation read
      // as an edge and the whole sky came back covered in a grid. It is
      // background: it has no edges and occludes nothing, so it sits out
      // the prepass and its pixels keep the colour they were given.
      const sky = scene.getObjectByName('skyDome');
      const skyWasVisible = sky ? sky.visible : false;
      if (sky) sky.visible = false;
      // CLEARANCE SHELLS SIT OUT THE PREPASS FOR THE SAME REASON THE SKY
      // DOES. This pass overrides every material to write depth and normals,
      // so a translucent shell writes them AS IF SOLID - and the edges and
      // the contact shade are derived from that buffer. Left in, every
      // clearance box gets an outline drawn round it and darkens whatever
      // sits behind it, which is precisely the "forced opaque" failure the
      // pool materials were fixed for on 2026-09-14. A see-through volume
      // has no edges and occludes nothing; it is not a surface.
      const hiddenForPrepass = [];
      scene.traverse((o) => {
        if (o && o.visible && o.userData && o.userData.isClearance) {
          hiddenForPrepass.push(o);
          o.visible = false;
        }
      });
      scene.overrideMaterial = this.normalMaterial;
      this.renderer.setRenderTarget(this.normal);
      this.renderer.clear(true, true, true);
      this.renderer.render(scene, camera);
      scene.overrideMaterial = prevOverride;
      for (const o of hiddenForPrepass) o.visible = true;
      if (sky) sky.visible = skyWasVisible;
      this._normalValidAt = key;
    }
    // 2. the picture as it would have been drawn
    this.renderer.setRenderTarget(this.color);
    this.renderer.clear(true, true, true);
    this.renderer.render(scene, camera);
    // 3. one screen pass onto the canvas - or into the composer's buffer,
    //    which is what lets the outline pass draw on top of the composited
    //    picture instead of replacing it.
    this.renderer.setRenderTarget(target);
    if (target) this.renderer.clear(true, true, true);
    this.renderer.render(this.quadScene, this.quadCamera);
    this.renderer.setRenderTarget(prevTarget);
    return true;
  }

  dispose(keepMaterial = false) {
    // The uniforms must let go of the textures too, or a disposed target
    // stays reachable through the shader and the memory never comes back.
    const u = this.material && this.material.uniforms;
    if (u) { u.tColor.value = null; u.tNormal.value = null; u.tDepth.value = null; }
    this._normalValidAt = null;
    if (this.color) { this.color.dispose(); this.color = null; }
    if (this.normal) {
      if (this.normal.depthTexture) this.normal.depthTexture.dispose();
      this.normal.dispose(); this.normal = null;
    }
    if (!keepMaterial) {
      this.material.dispose();
      this.quad.geometry.dispose();
      this.normalMaterial.dispose();
    }
  }
}
